import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { authenticateUser, logout } from "../redux/authSlice";

const SessionTimeout = () => {
    const [showWarning, setShowWarning] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const token = useSelector((state) => state.auth.token);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Refs to store timer IDs so we can clear them effectively
    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    // Configuration
    const WARNING_DURATION = 120000; // 2 minutes in milliseconds

    const clearAllTimers = useCallback(() => {
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }, []);

    const performLogout = useCallback(() => {
        clearAllTimers();
        setShowWarning(false);
        dispatch(logout());
        navigate("/login");
    }, [dispatch, navigate, clearAllTimers]);

    const handleRefreshToken = async () => {
        try {
            // Confirm the endpoint and method with backend team if needed
            // Based on request: @PostMapping("/auth/public/refresh-token")
            const response = await axios.post(`${process.env.REACT_APP_URL}/auth/public/refresh-token`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data && response.data.success) {
                // Update tokens
                const newToken = response.data.data.token; // Adjust according to actual response structure

                // Update session storage and redux
                sessionStorage.setItem("person", JSON.stringify(newToken));
                document.cookie = `isAuth=true; path=/`; // Ensure cookie is fresh if needed

                dispatch(authenticateUser({
                    isAuthenticated: true,
                    token: newToken
                }));

                // Close modal
                setShowWarning(false);
                // Effect will re-run because token changed, setting up new timers
            } else {
                console.error("Refresh token failed:", response.data.message);
                performLogout();
            }
        } catch (error) {
            console.error("Error refreshing token:", error);
            performLogout();
        }
    };

    useEffect(() => {
        if (!isAuthenticated || !token) {
            return;
        }

        try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now();
            const expTime = decoded.exp * 1000;

            // Calculate time until expiration
            const timeUntilExpiry = expTime - currentTime;

            // If already expired
            if (timeUntilExpiry <= 0) {
                performLogout();
                return;
            }

            // Calculate when to show warning
            // If token life is less than WARNING_DURATION, show warning immediately (or soon)
            // effective warning time should be min(timeUntilExpiry - 1000, timeUntilExpiry - WARNING_DURATION)
            // But usually we trigger warning 5 mins before.

            let timeUntilWarning = timeUntilExpiry - WARNING_DURATION;

            // If the token is already in the warning zone (e.g. user refreshed page with < 5 mins left)
            if (timeUntilWarning < 0) {
                timeUntilWarning = 0;
            }

            // Set timer to show warning
            clearAllTimers();

            warningTimerRef.current = setTimeout(() => {
                setShowWarning(true);

                // Start countdown for the modal UI
                // We need to sync this with the actual remaining time
                const startCountdown = () => {
                    const current = Date.now();
                    const remaining = Math.max(0, Math.ceil((expTime - current) / 1000));
                    setTimeLeft(remaining);

                    if (remaining <= 0) {
                        performLogout();
                    }
                };

                startCountdown();
                countdownIntervalRef.current = setInterval(startCountdown, 1000);

            }, timeUntilWarning);

            // Set ultimate logout timer as a fallback
            logoutTimerRef.current = setTimeout(() => {
                performLogout();
            }, timeUntilExpiry);

            return () => clearAllTimers();

        } catch (error) {
            console.error("Invalid token:", error);
            performLogout();
        }

    }, [token, isAuthenticated, performLogout, clearAllTimers]);

    if (!showWarning) return null;

    // Format time left
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle}>
                <h3>Session Expiring</h3>
                <p>Your session will expire in {minutes}:{seconds < 10 ? `0${seconds}` : seconds}.</p>
                <p>Do you want to stay logged in?</p>
                <div style={buttonContainerStyle}>
                    <button onClick={handleRefreshToken} style={confirmButtonStyle}>Stay Logged In</button>
                    <button onClick={performLogout} style={cancelButtonStyle}>Logout</button>
                </div>
            </div>
        </div>
    );
};

// Simple inline styles for the modal
const modalOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
};

const modalContentStyle = {
    backgroundColor: 'white',
    color: '#333', // Ensure text is visible
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    textAlign: 'center',
    minWidth: '300px'
};

const buttonContainerStyle = {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'space-around'
};

const confirmButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
};

const cancelButtonStyle = {
    padding: '8px 16px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
};

export default SessionTimeout;
